import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AUTH_ACTIVITY,
  authActivityDetail,
  initials,
  normalizeUsername,
  roleLabel,
  type Role,
} from '@agrogruda/shared';
import { firstIssueMessage, userUpsertForm } from '@agrogruda/shared/schemas';
import { isRole } from '@/auth/AuthProvider';
import { useAuth } from '@/auth/useAuth';
import { Button } from '@/components/ui/Button';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { FormGroup } from '@/components/ui/FormGroup';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/Toast';
import { ROLE_OPTION_ORDER, ROLE_OPTIONS, sq, userStatusLine } from '@/i18n/sq';
import { logActivity } from '@/lib/activity';
import { ApiError, errorMessage, usersApi, type CreateUserBody, type UpdateUserBody } from '@/lib/api';
import { supabase, type Profile } from '@/lib/supabase';

export const PROFILES_KEY = ['profiles'] as const;
export const EMPLOYEES_KEY = ['employees', 'options'] as const;

type FormMode = { kind: 'idle' } | { kind: 'edit'; id: string } | { kind: 'create' };

interface FormState {
  name: string;
  username: string;
  role: Role;
  password: string;
  employeeId: string;
  active: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  username: '',
  role: 'staff',
  password: '',
  employeeId: '',
  active: true,
};

interface EmployeeOption {
  id: string;
  name: string;
}

async function fetchProfiles(): Promise<Profile[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return data;
}

async function fetchEmployees(): Promise<EmployeeOption[]> {
  const { data, error } = await supabase
    .from('employees')
    .select('id,name')
    .order('name', { ascending: true });
  if (error) throw new Error(error.message);
  return data;
}

export interface UsersModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Prototype #usersModal (lines 1457-1471) + renderAuthUsers/resetUserForm/editAuthUser/saveAuthUser
 * (lines 3116-3128). Writes go through apps/api (POST/PATCH/DELETE /api/users) because passwords live in
 * Supabase Auth. New in the rebuild: 'Shto hyrje' creates additional logins (decision D7) and 'Fshij' removes
 * one, never the admin's own.
 */
export function UsersModal({ open, onClose }: UsersModalProps) {
  const { profile: actor, refreshProfile, signIn } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<FormMode>({ kind: 'idle' });
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const profiles = useQuery({ queryKey: PROFILES_KEY, queryFn: fetchProfiles, enabled: open });
  const employees = useQuery({ queryKey: EMPLOYEES_KEY, queryFn: fetchEmployees, enabled: open });

  const resetForm = useCallback(() => {
    setMode({ kind: 'idle' });
    setForm(EMPTY_FORM);
  }, []);

  // Prototype openUsersModal(): renderAuthUsers(); resetUserForm() on every open.
  useEffect(() => {
    if (open) resetForm();
  }, [open, resetForm]);

  const startEdit = (user: Profile) => {
    setMode({ kind: 'edit', id: user.id });
    setForm({
      name: user.name,
      username: user.username,
      role: isRole(user.role) ? user.role : 'staff',
      password: '',
      employeeId: user.employee_id ?? '',
      active: user.active,
    });
  };

  const startCreate = () => {
    setMode({ kind: 'create' });
    setForm(EMPTY_FORM);
  };

  /**
   * Typing into the form while nothing is selected means a new login. The fields sit there fully
   * editable with no entry chosen, so filling them in and pressing save is the obvious thing to do —
   * and it used to be refused at the very end with "pick one from the list", after all the typing.
   * An edit in progress is left alone: only the idle state is promoted.
   */
  const update = (patch: Partial<FormState>) => {
    setMode((current) => (current.kind === 'idle' ? { kind: 'create' } : current));
    setForm((current) => ({ ...current, ...patch }));
  };

  const afterSave = async (editedId: string | null) => {
    await queryClient.invalidateQueries({ queryKey: PROFILES_KEY });
    if (editedId !== null && editedId === actor?.id) await refreshProfile();
    resetForm();
    toast(sq.users.saved);
  };

  const failure = (error: unknown) => {
    if (error instanceof ApiError && error.code === 'username_taken') {
      toast(sq.users.usernameTaken);
      return;
    }
    toast(errorMessage(error));
  };

  const saveEdit = useMutation({
    mutationFn: async (input: {
      id: string;
      body: UpdateUserBody;
      role: Role;
      name: string;
      username: string;
    }) => {
      await usersApi.update(input.id, input.body);
      // Supabase deletes every session of a user whose password changes, so an admin who saved their own
      // login with a new password kept a dead access token: the next write answered 401 while the shell
      // still looked signed in (docs/BUGLOG.md B32). Sign in again with the password that was just set.
      if (input.id === actor?.id && input.body.password) {
        await signIn(input.username, input.body.password);
      }
      return input;
    },
    onSuccess: async (input) => {
      if (actor) {
        void logActivity(actor, 'auth', AUTH_ACTIVITY.userSaved, authActivityDetail(input.name, input.role));
      }
      await afterSave(input.id);
    },
    onError: failure,
  });

  const saveCreate = useMutation({
    mutationFn: async (body: CreateUserBody) => {
      await usersApi.create(body);
      return body;
    },
    onSuccess: async (body) => {
      if (actor) {
        void logActivity(actor, 'auth', sq.users.createdActivity, authActivityDetail(body.name, body.role));
      }
      await afterSave(null);
    },
    onError: failure,
  });

  const remove = useMutation({
    mutationFn: async (user: Profile) => {
      await usersApi.remove(user.id);
      return user;
    },
    onSuccess: async (user) => {
      // the API writes the "U fshi hyrja" activity itself
      await queryClient.invalidateQueries({ queryKey: PROFILES_KEY });
      resetForm();
      toast(sq.users.deleted(user.name));
    },
    onError: failure,
  });

  const busy = saveEdit.isPending || saveCreate.isPending || remove.isPending;

  const editedUser = mode.kind === 'edit' ? profiles.data?.find((u) => u.id === mode.id) : undefined;
  const canDelete = editedUser !== undefined && editedUser.id !== actor?.id;

  const askDelete = async () => {
    if (!editedUser || busy) return;
    const ok = await confirm(sq.users.deleteConfirm(editedUser.name, editedUser.username), {
      title: sq.users.deleteTitle,
      okLabel: sq.users.deleteOk,
      danger: true,
    });
    if (ok) remove.mutate(editedUser);
  };

  // Prototype saveAuthUser() (line 3125): toasts in the order name → username → duplicate → password.
  const submit = () => {
    if (busy) return;
    if (mode.kind === 'idle') {
      toast(sq.users.selectFirst);
      return;
    }
    const editedId = mode.kind === 'edit' ? mode.id : null;
    const list = profiles.data ?? [];
    const isTaken = (username: string) =>
      list.some((u) => u.username.toLowerCase() === username && u.id !== editedId);

    const parsed = userUpsertForm.safeParse({
      id: editedId ?? undefined,
      name: form.name,
      username: form.username,
      password: form.password,
      role: form.role,
      employee_id: form.employeeId ? form.employeeId : null,
    });
    if (!parsed.success) {
      // The duplicate check sits between the username and password checks in the prototype.
      const first = parsed.error.issues[0];
      if (first?.path[0] === 'password' && isTaken(normalizeUsername(form.username))) {
        toast(sq.users.usernameTaken);
        return;
      }
      toast(firstIssueMessage(parsed.error));
      return;
    }
    const { name, username, password, role, employee_id: employeeId } = parsed.data;
    if (isTaken(username)) {
      toast(sq.users.usernameTaken);
      return;
    }
    if (mode.kind === 'edit') {
      const body: UpdateUserBody = { name, username, active: form.active, employee_id: employeeId, role };
      if (password) body.password = password;
      saveEdit.mutate({ id: mode.id, body, role, name, username });
    } else if (password) {
      // userUpsertForm requires the password when no id is given, so this branch always runs for 'create'.
      saveCreate.mutate({ name, username, password, role, employee_id: employeeId });
    }
  };

  const formTitle =
    mode.kind === 'edit'
      ? sq.users.editTitle(editedUser?.name ?? form.name)
      : mode.kind === 'create'
        ? sq.users.createTitle
        : sq.users.idleTitle;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={sq.users.title}
      subtitle={sq.users.subtitle}
      wide
      id="usersModal"
      footer={<Button onClick={onClose}>{sq.common.close}</Button>}
    >
      <div className="user-management-grid">
        <div>
          <div className="user-list" id="usersList">
            {(profiles.data ?? []).map((user) => (
              <div className="user-item" key={user.id} data-username={user.username}>
                <div className="avatar" aria-hidden="true">
                  {initials(user.name)}
                </div>
                <div>
                  <b>{user.name}</b>
                  <small>{userStatusLine(user.username, user.active)}</small>
                </div>
                <div>
                  <div style={{ textAlign: 'right', marginBottom: 6 }}>
                    <span className={`role-badge ${user.role}`}>{roleLabel(user.role)}</span>
                  </div>
                  <div className="user-actions">
                    <Button
                      size="small"
                      onClick={() => startEdit(user)}
                      aria-label={`${sq.common.edit} ${user.name}`}
                    >
                      {sq.common.edit}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="user-form-card">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              marginBottom: 12,
            }}
          >
            <h4 id="userFormTitle" style={{ margin: 0 }}>
              {formTitle}
            </h4>
            <Button size="small" variant="soft" id="addUserBtn" onClick={startCreate}>
              {sq.users.addLogin}
            </Button>
          </div>
          <FormGroup label={sq.users.name} htmlFor="userFullName">
            <Input
              id="userFullName"
              placeholder={sq.users.namePlaceholder}
              autoComplete="off"
              value={form.name}
              onChange={(e) => update({ name: e.target.value })}
            />
          </FormGroup>
          <FormGroup label={sq.users.username} htmlFor="userUsername" style={{ marginTop: 10 }}>
            <Input
              id="userUsername"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder={sq.users.usernamePlaceholder}
              value={form.username}
              onChange={(e) => update({ username: e.target.value })}
            />
          </FormGroup>
          <FormGroup label={sq.users.role} htmlFor="userRole" style={{ marginTop: 10 }}>
            <Select
              id="userRole"
              value={form.role}
              disabled={mode.kind === 'edit' && mode.id === actor?.id}
              onChange={(e) => update({ role: isRole(e.target.value) ? e.target.value : 'staff' })}
            >
              {ROLE_OPTION_ORDER.map((role) => (
                <option key={role} value={role}>
                  {ROLE_OPTIONS[role]}
                </option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup label={sq.users.employee} htmlFor="userEmployee" style={{ marginTop: 10 }}>
            <Select
              id="userEmployee"
              value={form.employeeId}
              onChange={(e) => update({ employeeId: e.target.value })}
            >
              <option value="">{sq.users.noEmployee}</option>
              {(employees.data ?? []).map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup
            label={mode.kind === 'create' ? sq.users.passwordRequired : sq.users.newPassword}
            htmlFor="userPassword"
            labelId="userPasswordLabel"
            style={{ marginTop: 10 }}
          >
            <Input
              id="userPassword"
              type="password"
              autoComplete="new-password"
              placeholder={mode.kind === 'create' ? undefined : sq.users.newPasswordPlaceholder}
              value={form.password}
              onChange={(e) => update({ password: e.target.value })}
            />
          </FormGroup>
          {mode.kind === 'edit' ? (
            <label className="checkbox" htmlFor="userActive">
              <input
                type="checkbox"
                id="userActive"
                checked={form.active}
                onChange={(e) => update({ active: e.target.checked })}
              />
              {sq.users.active}
            </label>
          ) : null}
          <div className="actions" style={{ marginTop: 12 }}>
            <Button onClick={resetForm}>
              {mode.kind === 'create' ? sq.common.cancel : sq.users.cancelEdit}
            </Button>
            {/* Disabled while a save is in flight: `submit()` ignores clicks then, and an enabled button
                that silently does nothing loses the edit the admin just typed (docs/BUGLOG.md B31). */}
            <Button variant="primary" id="saveUserBtn" onClick={submit} disabled={busy}>
              {mode.kind === 'create' ? sq.users.create : sq.users.saveEdit}
            </Button>
            {canDelete ? (
              <Button
                variant="danger"
                className="user-delete-btn"
                onClick={() => void askDelete()}
                disabled={busy}
              >
                {sq.users.delete}
              </Button>
            ) : null}
          </div>
          <div className="permission-note">
            <b>{sq.users.permissionNote.adminLabel}</b> {sq.users.permissionNote.adminText}
            <br />
            <b>{sq.users.permissionNote.staffLabel}</b> {sq.users.permissionNote.staffText}
            <br />
            <b>{sq.users.permissionNote.deliveryLabel}</b> {sq.users.permissionNote.deliveryText}
          </div>
        </div>
      </div>
    </Modal>
  );
}
